import React, { useState, useEffect, useRef } from 'react';
import {
    Card,
    Table,
    Button,
    Input,
    Select,
    DatePicker,
    Space,
    Tag,
    Typography,
    Row,
    Col,
    Statistic,
    Alert,
    Modal,
    message,
    Tooltip,
    Badge,
    Divider,
    Collapse,
    Empty,
    Spin
} from 'antd';
import {
    SearchOutlined,
    DownloadOutlined,
    DeleteOutlined,
    ReloadOutlined,
    EyeOutlined,
    FilterOutlined,
    FileTextOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    WarningOutlined,
    BugOutlined
} from '@ant-design/icons';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

function LogViewer() {
    const [logFiles, setLogFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [logContent, setLogContent] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [logLevel, setLogLevel] = useState('ALL');
    const [dateRange, setDateRange] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [systemStats, setSystemStats] = useState(null);
    const [showRawLog, setShowRawLog] = useState(false);
    const [selectedLogEntry, setSelectedLogEntry] = useState(null);
    const [linesToShow, setLinesToShow] = useState(100);
    const logContainerRef = useRef(null);

    useEffect(() => {
        loadLogFiles();
        loadSystemStats();
    }, []);

    useEffect(() => {
        if (selectedFile) {
            loadLogContent();
        }
    }, [selectedFile, linesToShow, logLevel]);

    const loadLogFiles = async () => {
        try {
            setLoading(true);
            const response = await api.request('/admin/logs/files');
            if (response.success) {
                setLogFiles(response.data);
                if (response.data.length > 0 && !selectedFile) {
                    setSelectedFile(response.data[0].name);
                }
            }
        } catch (error) {
            message.error('Failed to load log files');
        } finally {
            setLoading(false);
        }
    };

    const loadLogContent = async () => {
        if (!selectedFile) return;

        try {
            setLoading(true);
            const response = await api.request(`/admin/logs/content?file=${selectedFile}&lines=${linesToShow}&level=${logLevel}`);
            if (response.success) {
                setLogContent(response.data.logs);
            }
        } catch (error) {
            message.error('Failed to load log content');
        } finally {
            setLoading(false);
        }
    };

    const loadSystemStats = async () => {
        try {
            const response = await api.request('/admin/logs/stats');
            if (response.success) {
                setSystemStats(response.data);
            }
        } catch (error) {
            console.error('Failed to load system stats:', error);
        }
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            message.warning('Please enter a search query');
            return;
        }

        try {
            setLoading(true);
            const params = {
                query: searchQuery,
                level: logLevel,
                file: selectedFile
            };

            if (dateRange && dateRange.length === 2) {
                params.date_from = dateRange[0].format('YYYY-MM-DD');
                params.date_to = dateRange[1].format('YYYY-MM-DD');
            }

            const response = await api.request('/admin/logs/search', {
                method: 'POST',
                body: JSON.stringify(params)
            });

            if (response.success) {
                setSearchResults(response.data.results);
            }
        } catch (error) {
            message.error('Search failed');
        } finally {
            setLoading(false);
        }
    };

    const handleClearLog = (fileName) => {
        Modal.confirm({
            title: 'Clear Log File',
            content: `Are you sure you want to clear the log file "${fileName}"? This action cannot be undone.`,
            icon: <ExclamationCircleOutlined />,
            okText: 'Yes, Clear',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    const response = await api.request(`/admin/logs/clear?file=${fileName}`, {
                        method: 'DELETE'
                    });
                    if (response.success) {
                        message.success('Log file cleared successfully');
                        loadLogContent();
                        loadSystemStats();
                    }
                } catch (error) {
                    message.error('Failed to clear log file');
                }
            }
        });
    };

    const handleDownloadLog = async (fileName) => {
        try {
            const response = await fetch(`/api/admin/logs/download?file=${fileName}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Accept': 'application/octet-stream'
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                message.success('Log file downloaded successfully');
            } else {
                message.error('Failed to download log file');
            }
        } catch (error) {
            message.error('Failed to download log file');
        }
    };

    const getLevelColor = (level) => {
        const colors = {
            ERROR: 'red',
            WARNING: 'orange',
            INFO: 'blue',
            DEBUG: 'green',
            CRITICAL: 'red'
        };
        return colors[level] || 'default';
    };

    const getLevelIcon = (level) => {
        const icons = {
            ERROR: <ExclamationCircleOutlined />,
            WARNING: <WarningOutlined />,
            INFO: <InfoCircleOutlined />,
            DEBUG: <BugOutlined />
        };
        return icons[level] || <InfoCircleOutlined />;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const logColumns = [
        {
            title: 'Level',
            dataIndex: 'level',
            key: 'level',
            width: 100,
            render: (level) => (
                <Tag color={getLevelColor(level)} icon={getLevelIcon(level)}>
                    {level}
                </Tag>
            ),
            filters: [
                { text: 'ERROR', value: 'ERROR' },
                { text: 'WARNING', value: 'WARNING' },
                { text: 'INFO', value: 'INFO' },
                { text: 'DEBUG', value: 'DEBUG' }
            ],
            onFilter: (value, record) => record.level === value
        },
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (timestamp) => timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : '-'
        },
        {
            title: 'Message',
            dataIndex: 'message',
            key: 'message',
            ellipsis: true,
            render: (message, record) => (
                <div>
                    <Text 
                        code 
                        style={{ 
                            fontSize: '12px',
                            backgroundColor: '#f5f5f5',
                            padding: '2px 4px',
                            borderRadius: '3px',
                            cursor: 'pointer'
                        }}
                        onClick={() => setSelectedLogEntry(record)}
                    >
                        {message.length > 100 ? message.substring(0, 100) + '...' : message}
                    </Text>
                </div>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => setSelectedLogEntry(record)}
                    size="small"
                />
            )
        }
    ];

    const fileColumns = [
        {
            title: 'File Name',
            dataIndex: 'name',
            key: 'name',
            render: (name) => (
                <Space>
                    <FileTextOutlined />
                    <Text strong={selectedFile === name}>{name}</Text>
                </Space>
            )
        },
        {
            title: 'Size',
            dataIndex: 'size',
            key: 'size',
            width: 100,
            render: (size) => formatFileSize(size)
        },
        {
            title: 'Modified',
            dataIndex: 'modified',
            key: 'modified',
            width: 150,
            render: (modified) => dayjs.unix(modified).format('YYYY-MM-DD HH:mm:ss')
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title="View">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => setSelectedFile(record.name)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Download">
                        <Button
                            type="text"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownloadLog(record.name)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Clear">
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={() => handleClearLog(record.name)}
                            size="small"
                            danger
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <div className="p-6">
            <div className="mb-6">
                <Title level={2}>
                    <FileTextOutlined className="mr-2" />
                    Log Viewer
                </Title>
                <Text type="secondary">
                    View and manage application logs
                </Text>
            </div>

            {/* System Stats */}
            {systemStats && (
                <Row gutter={16} className="mb-6">
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Total Files"
                                value={systemStats.total_files}
                                prefix={<FileTextOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Total Size"
                                value={formatFileSize(systemStats.total_size)}
                                prefix={<FileTextOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Errors"
                                value={systemStats.error_count}
                                valueStyle={{ color: '#cf1322' }}
                                prefix={<ExclamationCircleOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Warnings"
                                value={systemStats.warning_count}
                                valueStyle={{ color: '#fa8c16' }}
                                prefix={<WarningOutlined />}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            <Row gutter={16}>
                {/* Log Files */}
                <Col span={8}>
                    <Card
                        title="Log Files"
                        extra={
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadLogFiles}
                                size="small"
                            />
                        }
                    >
                        <Table
                            columns={fileColumns}
                            dataSource={logFiles}
                            rowKey="name"
                            size="small"
                            pagination={false}
                            loading={loading}
                            onRow={(record) => ({
                                onClick: () => setSelectedFile(record.name),
                                style: { cursor: 'pointer' }
                            })}
                        />
                    </Card>
                </Col>

                {/* Log Content */}
                <Col span={16}>
                    <Card
                        title={`Log Content - ${selectedFile || 'Select a file'}`}
                        extra={
                            <Space>
                                <Select
                                    value={linesToShow}
                                    onChange={setLinesToShow}
                                    style={{ width: 100 }}
                                    size="small"
                                >
                                    <Option value={50}>50 lines</Option>
                                    <Option value={100}>100 lines</Option>
                                    <Option value={500}>500 lines</Option>
                                    <Option value={1000}>1000 lines</Option>
                                </Select>
                                <Select
                                    value={logLevel}
                                    onChange={setLogLevel}
                                    style={{ width: 100 }}
                                    size="small"
                                >
                                    <Option value="ALL">All Levels</Option>
                                    <Option value="ERROR">Errors</Option>
                                    <Option value="WARNING">Warnings</Option>
                                    <Option value="INFO">Info</Option>
                                    <Option value="DEBUG">Debug</Option>
                                </Select>
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={loadLogContent}
                                    size="small"
                                />
                            </Space>
                        }
                    >
                        <div ref={logContainerRef} style={{ maxHeight: '600px', overflow: 'auto' }}>
                            {loading ? (
                                <div className="text-center py-8">
                                    <Spin size="large" />
                                </div>
                            ) : logContent.length === 0 ? (
                                <Empty description="No log entries found" />
                            ) : (
                                <Table
                                    columns={logColumns}
                                    dataSource={logContent}
                                    rowKey={(record, index) => index}
                                    size="small"
                                    pagination={{ pageSize: 50, showSizeChanger: false }}
                                    scroll={{ y: 500 }}
                                />
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Search Section */}
            <Card title="Search Logs" className="mt-6">
                <Row gutter={16}>
                    <Col span={8}>
                        <Search
                            placeholder="Search in logs..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onSearch={handleSearch}
                            enterButton={<SearchOutlined />}
                            loading={loading}
                        />
                    </Col>
                    <Col span={4}>
                        <Select
                            value={logLevel}
                            onChange={setLogLevel}
                            style={{ width: '100%' }}
                            placeholder="Log Level"
                        >
                            <Option value="ALL">All Levels</Option>
                            <Option value="ERROR">Errors</Option>
                            <Option value="WARNING">Warnings</Option>
                            <Option value="INFO">Info</Option>
                            <Option value="DEBUG">Debug</Option>
                        </Select>
                    </Col>
                    <Col span={6}>
                        <RangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={6}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            loading={loading}
                            block
                        >
                            Search
                        </Button>
                    </Col>
                </Row>

                {searchResults.length > 0 && (
                    <div className="mt-4">
                        <Divider />
                        <Title level={4}>Search Results ({searchResults.length})</Title>
                        <Table
                            columns={logColumns}
                            dataSource={searchResults}
                            rowKey={(record, index) => index}
                            size="small"
                            pagination={{ pageSize: 20 }}
                        />
                    </div>
                )}
            </Card>

            {/* Log Entry Detail Modal */}
            <Modal
                title="Log Entry Details"
                open={!!selectedLogEntry}
                onCancel={() => setSelectedLogEntry(null)}
                footer={[
                    <Button key="close" onClick={() => setSelectedLogEntry(null)}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                {selectedLogEntry && (
                    <div>
                        <Row gutter={16} className="mb-4">
                            <Col span={8}>
                                <Text strong>Level: </Text>
                                <Tag color={getLevelColor(selectedLogEntry.level)} icon={getLevelIcon(selectedLogEntry.level)}>
                                    {selectedLogEntry.level}
                                </Tag>
                            </Col>
                            <Col span={16}>
                                <Text strong>Timestamp: </Text>
                                <Text code>
                                    {selectedLogEntry.timestamp ? dayjs(selectedLogEntry.timestamp).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}
                                </Text>
                            </Col>
                        </Row>
                        <Divider />
                        <div>
                            <Text strong>Message:</Text>
                            <div style={{ 
                                backgroundColor: '#f5f5f5', 
                                padding: '12px', 
                                borderRadius: '4px',
                                marginTop: '8px',
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all'
                            }}>
                                {selectedLogEntry.raw || selectedLogEntry.message}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default LogViewer;
